/* eslint-disable react/prop-types */
import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CampaignReadyModal,
  DiscountModal,
  ExitFeedbackModal,
  PaymentSuccessModal,
} from '../../../../components/CampaignUpsellModals'
import {
  UPSELL_FLOWS,
  useUpsellCatalog,
  useUpgradePlansTab,
  useUpsellEventMap,
  useUpsellFlowState,
  useUpsellCheckout,
  useUpsellSuccessClose,
  trackUpsellExitFeedback,
  resolvePlanCheckoutPayload,
  resolvePackageCheckoutPayload,
} from '../UpsellFlows'
import {
  buildMarketComparisonPrice,
  buildPlanMarketComparisonPrice,
  UPSELL_FEATURED_PACKAGE_CODE,
  UPSELL_FEATURED_PLAN_TIER,
  filterStaticPlanOptions,
} from '../UpsellFlows/upsellCatalog'
import {
  PLANS_OTHER_OPTIONS,
  PRO_UPGRADE,
  SUBSCRIBED_ARTS_LIMIT_STEPS,
  SUBSCRIBED_UPGRADE_BONUS_OFFER_CODE,
} from './constants'

export const SubscribedArtsLimitUpsellFlow = forwardRef(
  function SubscribedArtsLimitUpsellFlow(
    { enabled = false, campaignId = null, onDismiss },
    ref,
  ) {
    const { t } = useTranslation()
    const catalog = useUpsellCatalog({ enabled })
    const {
      openSubscriptionCheckout,
      openAddonCheckout,
    } = useUpsellCheckout()
    const {
      activeTab,
      setActiveTab,
      closeFlow: resetFlow,
      openFlow,
      isStep,
      goToStep,
      openSuccessStep,
      currentStep,
      isSuccessStepActive,
    } = useUpsellFlowState({
      enabled,
      initialStep: SUBSCRIBED_ARTS_LIMIT_STEPS.READY,
      initialTab: 'plans',
    })
    const upsellTabs = useUpgradePlansTab(
      catalog,
      activeTab,
      setActiveTab,
      [
        { id: 'packages', label: t('upsell.tabs.packages') },
        { id: 'plans', label: t('upsell.tabs.plans') },
      ],
    )

    const exitFeedbackOptions = useMemo(
      () => [
        { id: 'price', label: t('upsell.exit_feedback.price') },
        { id: 'trial', label: t('upsell.exit_feedback.trial') },
        { id: 'quality', label: t('upsell.exit_feedback.quality') },
        { id: 'exploring', label: t('upsell.exit_feedback.exploring') },
      ],
      [t],
    )

    useUpsellEventMap({
      flow: UPSELL_FLOWS.SUBSCRIBED_ARTS_LIMIT,
      campaignId,
      currentStep,
      activeTab,
    })

    const upgradeOffer = useMemo(
      () => catalog.buildUpgradeFeaturedOffer(),
      [catalog],
    )

    const rechargeOffer = useMemo(
      () => catalog.buildRechargeFeaturedOffer(),
      [catalog],
    )

    const packageMarketPrice = useMemo(
      () => buildMarketComparisonPrice(catalog.featuredPackage),
      [catalog.featuredPackage],
    )

    const planMarketPrice = useMemo(
      () => buildPlanMarketComparisonPrice(
        catalog.featuredUpgradePlan ?? catalog.featuredPlan,
      ),
      [catalog.featuredPlan, catalog.featuredUpgradePlan],
    )

    const featuredUpgradePlan = catalog.featuredUpgradePlan

    const plansOtherOptions = useMemo(() => {
      const featuredTier =
        catalog.featuredUpgradePlan?.tier ?? UPSELL_FEATURED_PLAN_TIER

      if (!catalog.dbPlans.length) {
        return filterStaticPlanOptions(
          PLANS_OTHER_OPTIONS,
          catalog.currentPlanTier,
          featuredTier,
        )
      }

      return catalog.getUpgradePlanOptions()
    }, [catalog])

    const packageOptions = useMemo(
      () => catalog.getPackageOptions([UPSELL_FEATURED_PACKAGE_CODE]),
      [catalog],
    )

    const rechargeArts = rechargeOffer?.arts
    const upgradeBonusOffer = useMemo(
      () => ({
        label: upgradeOffer?.artsPerMonth
          ? t('upsell.subscribed_arts_limit.upgrade_bonus_label', {
            plan: upgradeOffer.name ?? PRO_UPGRADE.name,
            count: upgradeOffer.artsPerMonth,
          })
          : t('upsell.subscribed_arts_limit.upgrade_bonus_label_fallback'),
        price: upgradeOffer?.price,
        subdescription: t('upsell.subscribed_arts_limit.upgrade_bonus_subdescription'),
      }),
      [t, upgradeOffer],
    )

    const rechargeDownsellOffer = useMemo(
      () => ({
        label: rechargeOffer?.label,
        description:
          rechargeArts != null
            ? t('upsell.arts_count', { count: rechargeArts })
            : rechargeOffer?.description,
        price: rechargeOffer?.price,
        subdescription: rechargeOffer?.subdescription,
      }),
      [rechargeArts, rechargeOffer, t],
    )

    const rechargeCtaLabel = rechargeArts != null
      ? t('upsell.subscribed_arts_limit.recharge_cta', { count: rechargeArts })
      : t('upsell.subscribed_arts_limit.recharge_cta_fallback')

    const hideFlow = resetFlow

    const closeFlow = useCallback(() => {
      resetFlow()
      onDismiss?.()
    }, [onDismiss, resetFlow])

    const closeSuccessFlow = useUpsellSuccessClose(closeFlow, isSuccessStepActive)

    const handleOpenPackageCheckout = useCallback((option) => {
      openAddonCheckout({
        ...resolvePackageCheckoutPayload(
          option,
          catalog.featuredPackage,
          UPSELL_FEATURED_PACKAGE_CODE,
        ),
        campaignArtGenerationId: campaignId,
        upsellFlow: UPSELL_FLOWS.SUBSCRIBED_ARTS_LIMIT,
        upsellSuccessStep: SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_RECHARGE,
        onOpen: hideFlow,
      })
    }, [campaignId, catalog.featuredPackage, hideFlow, openAddonCheckout])

    const handleOpenPlanCheckout = useCallback((
      option,
      {
        upsellSuccessStep = SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE,
        offerCode = null,
      } = {},
    ) => {
      openSubscriptionCheckout({
        ...resolvePlanCheckoutPayload(option, featuredUpgradePlan),
        campaignArtGenerationId: campaignId,
        upsellFlow: UPSELL_FLOWS.SUBSCRIBED_ARTS_LIMIT,
        upsellSuccessStep,
        offerCode,
        onOpen: hideFlow,
      })
    }, [campaignId, featuredUpgradePlan, hideFlow, openSubscriptionCheckout])

    const handleOpenFeaturedPackageCheckout = useCallback(() => {
      handleOpenPackageCheckout(catalog.featuredPackage)
    }, [catalog.featuredPackage, handleOpenPackageCheckout])

    const handleOpenFeaturedPlanCheckout = useCallback(() => {
      handleOpenPlanCheckout(featuredUpgradePlan, {
        upsellSuccessStep: SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE,
      })
    }, [featuredUpgradePlan, handleOpenPlanCheckout])

    const handleOpenUpgradeBonusPlanCheckout = useCallback(() => {
      handleOpenPlanCheckout(featuredUpgradePlan, {
        upsellSuccessStep: SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE_BONUS,
        offerCode: SUBSCRIBED_UPGRADE_BONUS_OFFER_CODE,
      })
    }, [featuredUpgradePlan, handleOpenPlanCheckout])

    const handleOpenPlanFromList = useCallback((option) => {
      handleOpenPlanCheckout(option, {
        upsellSuccessStep: SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE,
      })
    }, [handleOpenPlanCheckout])

    const withClose = (label) => ({
      label,
      onClick: closeSuccessFlow,
    })

    useImperativeHandle(ref, () => ({
      open: openFlow,
      close: closeFlow,
      openSuccessStep,
    }), [closeFlow, openFlow, openSuccessStep])

    if (!enabled && !isSuccessStepActive) {
      return null
    }

    const readyTitle = t('upsell.subscribed_arts_limit.title')
    const readySubtitle = t('upsell.subscribed_arts_limit.subtitle')

    const plansContent = {
      title: readyTitle,
      subtitle: readySubtitle,
      marketLabel: t('upsell.arts_limit.market_label_plans'),
      marketPrice: planMarketPrice,
      platformLabel: upgradeOffer?.name
        ? t('upsell.subscribed_arts_limit.upgrade_label', { plan: upgradeOffer.name })
        : t('upsell.subscribed_arts_limit.upgrade_label_fallback'),
      platformPrice: upgradeOffer?.price,
      featuredOffer: upgradeOffer,
      otherOptionsTitle: t('upsell.subscribed_arts_limit.other_options'),
      otherOptions: plansOtherOptions,
      onSelectOption: handleOpenPlanFromList,
      primaryAction: {
        label: upgradeOffer?.name
          ? t('upsell.subscribed_arts_limit.upgrade_cta', { plan: upgradeOffer.name })
          : t('upsell.subscribed_arts_limit.upgrade_cta_fallback'),
        onClick: handleOpenFeaturedPlanCheckout,
      },
      tertiaryLink: {
        label: t('upsell.subscribed_arts_limit.packages_link'),
        onClick: () => setActiveTab('packages'),
      },
      skipAction: {
        label: t('upsell.subscribed_arts_limit.skip_generating'),
        onClick: () => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.UPGRADE_BONUS),
      },
    }

    const packagesContent = {
      title: readyTitle,
      subtitle: readySubtitle,
      marketLabel: t('upsell.arts_limit.market_label_packages'),
      marketPrice: packageMarketPrice
        ? t('upsell.market_from_price', { price: packageMarketPrice })
        : null,
      platformLabel: t('upsell.arts_limit.platform_label'),
      platformPrice: rechargeOffer?.price,
      featuredOffer: rechargeOffer,
      otherOptionsTitle: t('upsell.subscribed_arts_limit.other_packages_title'),
      otherOptions: packageOptions,
      onSelectOption: handleOpenPackageCheckout,
      primaryAction: {
        label: rechargeCtaLabel,
        onClick: handleOpenFeaturedPackageCheckout,
      },
      skipAction: {
        label: t('upsell.subscribed_arts_limit.skip_generating'),
        onClick: () => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.UPGRADE_BONUS),
      },
    }

    return (
      <>
        <CampaignReadyModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.READY)}
          onClose={() => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.UPGRADE_BONUS)}
          tabs={upsellTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          packages={packagesContent}
          plans={plansContent}
        />

        <DiscountModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.UPGRADE_BONUS)}
          onClose={() => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.RECHARGE)}
          title={t('upsell.subscribed_arts_limit.upgrade_bonus_title')}
          subtitle={t('upsell.subscribed_arts_limit.upgrade_bonus_subtitle')}
          offer={upgradeBonusOffer}
          primaryAction={{
            label: t('upsell.subscribed_arts_limit.upgrade_bonus_cta'),
            onClick: handleOpenUpgradeBonusPlanCheckout,
          }}
          dismissAction={{
            label: t('upsell.subscribed_arts_limit.not_now'),
            onClick: () => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.RECHARGE),
          }}
        />

        <DiscountModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.RECHARGE)}
          onClose={() => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.EXIT)}
          title={t('upsell.subscribed_arts_limit.recharge_title')}
          subtitle={t('upsell.subscribed_arts_limit.recharge_subtitle')}
          offer={rechargeDownsellOffer}
          footnote={t('upsell.subscribed_arts_limit.recharge_footnote')}
          primaryAction={{
            label: rechargeCtaLabel,
            onClick: handleOpenFeaturedPackageCheckout,
          }}
          dismissAction={{
            label: t('upsell.arts_limit.exit_anyway'),
            onClick: () => goToStep(SUBSCRIBED_ARTS_LIMIT_STEPS.EXIT),
          }}
        />

        <ExitFeedbackModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.EXIT)}
          onClose={closeFlow}
          title={t('upsell.subscribed_arts_limit.exit_title')}
          description={t('upsell.subscribed_arts_limit.exit_description')}
          feedback={{
            options: exitFeedbackOptions,
          }}
          onExitWithFeedback={(reason) =>
            trackUpsellExitFeedback({
              flow: UPSELL_FLOWS.SUBSCRIBED_ARTS_LIMIT,
              campaignId,
              reasonId: reason.id,
              reasonLabel: reason.label,
            })
          }
          secondaryAction={{
            label: t('upsell.arts_limit.back_to_platform'),
            onClick: closeFlow,
          }}
        />

        <PaymentSuccessModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE)}
          onClose={closeSuccessFlow}
          title={t('upsell.subscribed_arts_limit.success_upgrade_title')}
          description={t('upsell.subscribed_arts_limit.success_upgrade_description', {
            plan: upgradeOffer?.name ?? PRO_UPGRADE.name,
            count: upgradeOffer?.artsPerMonth ?? PRO_UPGRADE.artsPerMonth,
          })}
          primaryAction={withClose(t('upsell.subscribed_arts_limit.continue_creating'))}
        />

        <PaymentSuccessModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_UPGRADE_BONUS)}
          onClose={closeSuccessFlow}
          title={t('upsell.subscribed_arts_limit.success_upgrade_title')}
          description={t('upsell.subscribed_arts_limit.success_upgrade_bonus_description', {
            plan: upgradeOffer?.name ?? PRO_UPGRADE.name,
            count: upgradeOffer?.artsPerMonth ?? PRO_UPGRADE.artsPerMonth,
          })}
          primaryAction={withClose(t('upsell.subscribed_arts_limit.continue_creating'))}
        />

        <PaymentSuccessModal
          isOpen={isStep(SUBSCRIBED_ARTS_LIMIT_STEPS.SUCCESS_RECHARGE)}
          onClose={closeSuccessFlow}
          title={t('upsell.subscribed_arts_limit.success_recharge_title')}
          description={
            rechargeArts != null
              ? t('upsell.subscribed_arts_limit.success_recharge_description', {
                count: rechargeArts,
              })
              : t('upsell.subscribed_arts_limit.success_recharge_description_fallback')
          }
          primaryAction={withClose(t('upsell.subscribed_arts_limit.continue_creating'))}
        />
      </>
    )
  },
)

export default SubscribedArtsLimitUpsellFlow
